import React, { useState, useRef } from "react";
import { Form, Input, Button, Select, message } from "antd";
import { getAllDeliveriesAction, initiateRefundAction, orderSearchAction } from "@app/store/slices/userSlice";
import { useAppDispatch } from "@app/hooks/reduxHooks";

const { Option } = Select;

const ManageRefund = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(undefined);
    const [amount, setAmount] = useState(null);
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();

    const debounceRef = useRef<NodeJS.Timeout | null>(null); // Debounce reference
    const [pagination, setPagination] = useState({ current: 1, pageSize: 1000000000000000 });

    const params = {

        current: pagination.current,
        pageSize: pagination.pageSize,
    };
    // Debounced API call for order search
    const fetchOrders = (search: string) => {
        if (!search) return;
        params.orderId = search;
        // Clear previous timeout if user keeps typing
        if (debounceRef.current) clearTimeout(debounceRef.current);

        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await dispatch(getAllDeliveriesAction(params)).unwrap();
                setOrders(data || []);
            } catch (error) {
                message.error("Failed to fetch orders.");
            }
            setLoading(false);
        }, 500); // Debounce delay (500ms)
    };

    const handleOrderSelect = (orderId: string) => {
        const order = orders.find((o) => o.orderId === orderId);
        console.log('order', order)
        setSelectedOrder(order);
        setAmount(calculateAmountOfDelivery(order));
        form.setFieldsValue({ amount: calculateAmountOfDelivery(order) || "" });
    };
    const calculateAmountOfDelivery = (record: any) => {
        if (!record) return 0;
        if (record.order_type == 'Subscription') {
            const isHistorical = record.status && record.status !== 'Scheduled';

            if (isHistorical) {
                // For historic deliveries (Delivered, Forcefully Completed, Cancelled, Skipped, Refunded, etc.):
                // Preserve historic locked product_price or date amount, do not overwrite with latest product sale_price
                if (record.product_price !== undefined && record.product_price !== null) {
                    return record.product_price * (record.quantity || 1);
                }
                if (record.amount !== undefined && record.amount !== null && record.amount > 0) {
                    return record.amount;
                }
            }

            // For Scheduled (future/ongoing) deliveries: reflect current updated product/variant price
            if (record?.product?.isVariantEnabled) {
                return (record.selectedOption?.sale_price || record.product_price || 0) * (record.quantity || 1);
            } else {
                return (record.product?.sale_price || record.product_price || 0) * (record.quantity || 1);
            }
        } else {
            return record.amount ?? 0;
        }
    };
    const handleSubmit = async (values: any) => {
        try {
            const response = await dispatch(initiateRefundAction(values)).unwrap();
            if (response.success) {
                message.success("Refund request submitted successfully!");
                form.resetFields();
                setSelectedOrder(null);
            } else {
                message.error(response.message || "Failed to submit refund request.");
            }
        } catch (error) {
            message.error("Failed to submit refund request.");
        }
    };

    return (
        <div style={{ maxWidth: 400, margin: "auto", padding: 20 }}>
            <h2>Refund Request</h2>
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                {/* Order ID Dropdown */}
                <Form.Item
                    label="Order ID"
                    name="orderId"
                    rules={[{ required: true, message: "Please select an order" }]}
                >
                    <Select
                        showSearch
                        placeholder="Search Order ID"
                        onSearch={fetchOrders}
                        onSelect={handleOrderSelect}
                        loading={loading}
                        filterOption={false}
                    >
                        {orders.map((order) => (
                            <Option key={order.orderId} value={order.orderId}>
                                {order.orderId}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* Amount (Read-only) */}
                <Form.Item
                    label="Amount"
                    name="amount"
                    rules={[{ required: true, message: "Amount is required" }]}
                >
                    <Input value={amount || ""} type="number" disabled={selectedOrder?.subscription_id} placeholder="Auto-filled from order" />
                </Form.Item>

                {/* Refund Reason */}
                <Form.Item
                    label="Refund Reason"
                    name="refundReason"
                    rules={[{ required: true, message: "Please enter a refund reason" }]}
                >
                    <Input.TextArea rows={3} placeholder="Enter refund reason" />
                </Form.Item>

                {/* Submit Button */}
                <Form.Item>
                    <Button type="primary" htmlType="submit" block>
                        Submit Refund
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ManageRefund;
